import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Building2, Users, ArrowRight } from 'lucide-react';
interface LoginPageProps {
  onLogin: (userType: 'broker' | 'developer', email: string) => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<'broker' | 'developer' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleUserTypeSelect = (type: 'broker' | 'developer') => {
    setSelectedUserType(type);
    setIsLoading(true);
    // Simulate quick login
    setTimeout(() => {
      setIsLoading(false);
      // Auto-login with demo credentials
      const demoEmail = type === 'broker' ? 'broker@demo.com' : 'developer@demo.com';
      onLogin(type, demoEmail);
    }, 800);
  };
  const handleCredentialLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      alert('Please enter your email and password');
      return;
    }
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      onLogin(selectedUserType || 'broker', email);
    }, 1500);
  };
  if (showCredentialForm && selectedUserType) {
    return <div className="min-h-screen bg-gradient-to-br from-primary via-primary-dark to-accent flex items-center justify-center p-4 font-sans" data-id="element-1963">
        <div className="w-full max-w-md" data-id="element-1964">
          {/* Logo/Brand */}
          <div className="text-center mb-8" data-id="element-1965">
            <div className="w-20 h-20 bg-white rounded-2xl shadow-xl mx-auto mb-4 flex items-center justify-center" data-id="element-1966">
              <img src="https://cdn.magicpatterns.com/uploads/ompKNW2ZKiat5oXcKrq3mX/white_logo_.svg" alt="Logo" className="w-12 h-12" style={{
              filter: 'invert(1)'
            }} data-id="element-1967" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1968">Welcome Back</h1>
            <p className="text-white/80 text-sm" data-id="element-1969">
              Sign in as{' '}
              {selectedUserType === 'broker' ? 'Broker' : 'Developer'}
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-1970">
            {/* Login Form */}
            <form onSubmit={handleCredentialLogin} className="space-y-5" data-id="element-1971">
              {/* Email Input */}
              <div data-id="element-1972">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1973">
                  Email Address
                </label>
                <div className="relative" data-id="element-1974">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1975" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" data-id="element-1976" />
                </div>
              </div>

              {/* Password Input */}
              <div data-id="element-1977">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1978">
                  Password
                </label>
                <div className="relative" data-id="element-1979">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1980" />
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" data-id="element-1981" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1982">
                    {showPassword ? <EyeOff className="w-5 h-5" data-id="element-1983" /> : <Eye className="w-5 h-5" data-id="element-1984" />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between" data-id="element-1985">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1986">
                  <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" data-id="element-1987" />
                  <span className="text-sm text-gray-600" data-id="element-1988">Remember me</span>
                </label>
                <button type="button" className="text-sm font-semibold text-primary hover:text-primary-dark transition-colors" data-id="element-1989">
                  Forgot Password?
                </button>
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1990">
                {isLoading ? <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1991" />
                    Signing in...
                  </> : 'Sign in'}
              </button>

              {/* Back Button */}
              <button type="button" onClick={() => {
              setShowCredentialForm(false);
              setSelectedUserType(null);
            }} className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors" data-id="element-1992">
                Back to user type selection
              </button>
            </form>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-primary via-primary-dark to-accent flex items-center justify-center p-4 font-sans" data-id="element-1993">
      <div className="w-full max-w-md" data-id="element-1994">
        {/* Logo/Brand */}
        <div className="text-center mb-8" data-id="element-1995">
          <div className="w-20 h-20 bg-white rounded-2xl shadow-xl mx-auto mb-4 flex items-center justify-center" data-id="element-1996">
            <img src="https://cdn.magicpatterns.com/uploads/ompKNW2ZKiat5oXcKrq3mX/white_logo_.svg" alt="Logo" className="w-12 h-12" style={{
            filter: 'invert(1)'
          }} data-id="element-1997" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1998">Welcome</h1>
          <p className="text-white/80 text-sm" data-id="element-1999">
            Choose your account type to continue
          </p>
        </div>

        {/* User Type Selection Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-2000">
          <h2 className="text-xl font-bold text-neutral-text mb-6 text-center" data-id="element-2001">
            I am a
          </h2>

          <div className="space-y-4" data-id="element-2002">
            {/* Broker Option */}
            <button onClick={() => handleUserTypeSelect('broker')} disabled={isLoading && selectedUserType === 'broker'} className="w-full p-6 rounded-2xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2003">
              <div className="flex items-center gap-4" data-id="element-2004">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-2005">
                  <Users className="w-8 h-8 text-white" data-id="element-2006" />
                </div>
                <div className="flex-1 text-left" data-id="element-2007">
                  <h3 className="text-lg font-bold text-neutral-text mb-1" data-id="element-2008">
                    Broker
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-2009">
                    Access client management, properties, and network
                  </p>
                </div>
                {isLoading && selectedUserType === 'broker' ? <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" data-id="element-2010" /> : <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-primary transition-colors" data-id="element-2011" />}
              </div>
            </button>

            {/* Developer Option */}
            <button onClick={() => handleUserTypeSelect('developer')} disabled={isLoading && selectedUserType === 'developer'} className="w-full p-6 rounded-2xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2012">
              <div className="flex items-center gap-4" data-id="element-2013">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-2014">
                  <Building2 className="w-8 h-8 text-white" data-id="element-2015" />
                </div>
                <div className="flex-1 text-left" data-id="element-2016">
                  <h3 className="text-lg font-bold text-neutral-text mb-1" data-id="element-2017">
                    Developer
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-2018">
                    Manage projects, brokers, and RMI requests
                  </p>
                </div>
                {isLoading && selectedUserType === 'developer' ? <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" data-id="element-2019" /> : <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-accent transition-colors" data-id="element-2020" />}
              </div>
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6" data-id="element-2021">
            <div className="absolute inset-0 flex items-center" data-id="element-2022">
              <div className="w-full border-t border-gray-200" data-id="element-2023"></div>
            </div>
            <div className="relative flex justify-center text-sm" data-id="element-2024">
              <span className="px-4 bg-white text-gray-500" data-id="element-2025">Or</span>
            </div>
          </div>

          {/* Sign in with credentials link */}
          <button onClick={() => setShowCredentialForm(true)} className="w-full text-center text-sm text-gray-600 hover:text-primary font-medium transition-colors" data-id="element-2026">
            Sign in with email and password
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-white/60 mt-6" data-id="element-2027">
          By continuing, you agree to our{' '}
          <button className="underline hover:text-white transition-colors" data-id="element-2028">
            Terms
          </button>{' '}
          and{' '}
          <button className="underline hover:text-white transition-colors" data-id="element-2029">
            Privacy Policy
          </button>
        </p>
      </div>
    </div>;
}